"use client";

import type { ReactNode } from "react";
import {
  type MentionableSearchResult,
  searchMentionables,
} from "@/actions/mentions";
import {
  SharedUserTagPicker,
  type SharedUserTagSuggestion,
} from "@/components/user-tag-picker/shared-user-tag-picker";

export type UserTagSuggestion = {
  id: string;
  handle: string;
  label: string;
  subtitle?: string | null;
  avatarUrl?: string | null;
};

type UserTagPickerProps = {
  disabled?: boolean;
  onSelect: (suggestion: UserTagSuggestion) => void;
  buttonLabel?: ReactNode;
  selectedUserIds?: Set<string>;
  currentUserId?: string;
  className?: string;
  placeholder?: string;
};

export function UserTagPicker({
  disabled,
  onSelect,
  buttonLabel = "Tag user",
  selectedUserIds,
  currentUserId,
  className,
  placeholder = "Search users...",
}: UserTagPickerProps) {
  return (
    <SharedUserTagPicker<UserTagSuggestion & SharedUserTagSuggestion>
      queryKey={["user-tag-search", currentUserId]}
      searchFn={async (trimmedQuery) => {
        const result = await searchMentionables(trimmedQuery);
        if (!result.ok) return [];
        // Filter to only users and exclude current user
        return result.data
          .filter(
            (item): item is MentionableSearchResult & { type: "user" } =>
              item.type === "user" && item.id !== currentUserId,
          )
          .map((item) => ({
            id: item.id,
            handle: item.handle,
            label: item.label,
            subtitle: item.subtitle,
            avatarUrl: item.avatarUrl,
          }));
      }}
      onSelect={onSelect}
      disabled={disabled}
      buttonLabel={buttonLabel}
      selectedUserIds={selectedUserIds}
      currentUserId={currentUserId}
      className={className}
      placeholder={placeholder}
      title="Tag a user"
      groupHeading="Users"
      emptyLabel="No users found"
      duplicateLabel="Already tagged"
    />
  );
}
